import React, { useState, useEffect } from 'react';

import {useMutation, useLazyQuery, useQuery} from '@apollo/client';

import {useNavigate, useParams} from 'react-router-dom';

import './patient.assign.scss';
import FormValidation from '../../forms/validation/formValidation';
import Button from '../../forms/buttons/Button';
import Input from '../../forms/inputs/Input';
import DateSelect from '../../forms/dates/dateSelect';
import Select from '../../forms/inputs/Select';
import { PatientAvatarUpload } from '../../visuals/Patient.avatar.uploader';
import gqlSchemaLibrary from '../../services/graphql.schemas';
import { clearGqlTypename } from '../../utils/clearTypename';
import CreateUserPhoneList from '../../users/assignment/Users.create.phoneList';
import Checkbox from '../../forms/inputs/Checkboxk';
import CreateUserAddressList from '../../users/assignment/Users.create.addressList';
import PatientEmergencyContacts from './patient.emergencyContact';
import {useTranslation} from "react-i18next";
import AuditTrail from "../../services/interactions/audit.trail";
import {PageViewLog} from "../../services/interactions/page.view";

const userCoreFields = {
    'bio.lastname': { type: 'text', required: true, value: ''},
    'bio.firstname': { type: 'text', required: true, value: '' },
    'bio.gender': { type: 'text', required: true, value: null },
    'email': { type: 'email', required: false, value: '' }
};

const patientFields = {
    'dateOfBirth': { type: 'date',  required: true, value: null},
    'EMPI': { type: 'text', value: '' },
    'MRN': { type: 'text', value: '' },
    'contactCareGiver': { type: 'bool', value: false },
    'bioReport': { type: 'bool', value: false },
    'phones': { type: 'array-phones', required: true, arrayRequired: 1, value: null },
    'address': { type: 'array-items', required: true,  arrayRequired: 1, value: null },
    'emergencyContacts': { type: 'array-custom',  required: true, arrayRequired: -1, value: null,
        fields : {
            'contactType': { type: 'text', required: true },
            'firstname': { type: 'text', required: true },
            'lastname': { type: 'text', required: true },
            'relationship': { type: 'text', required: true },
            'email': {
                type: 'email',
                required: false
            },
            'phones': {
                type: 'array-phones',
                arrayRequired: 1,
                required: (data) => {
                    return data['relationship'] === 'orderingProvider' || data['contactType'] === 'EmergencyOnly';
                }
            }
        }
    }
};

const fields = Object.assign({}, userCoreFields, patientFields);

function PatientAssign({ event, currentUser, match }) {
    let {patientid} = useParams();
    const {t} = useTranslation(['participants','users','common']);
    const navigate = useNavigate();
    const [isEditting, setIsEditting] = useState(false);
    const [edittingIds, setEdittingIds] = useState({ userId: '', patientId: '' });
    const [person, setPerson] = useState(null);
    const [currentOrg, setCurrentOrg] = useState(null);
    const [defaultCountry, setDefaultCountry] = useState(null);

    const [dataFields, setDataFields] = useState(fields);
    const [formValid, setFormValid] = useState(false);

    const [patient, setPatient] = useState({});
    const [userCore, setUserCore] = useState({});

    // Redirect to PatientList
    const [__submitting, __setSubmitting] = useState(false);

    const [requiredDevices, setRequiredDevices] = useState(false);
    const [deviceList, setDeviceList] = useState([]);

    useEffect(() => {
        if (currentOrg && currentOrg.participantForm && currentOrg.participantForm.requiredDevices) {
            const ds = currentOrg.participantForm.requiredDevices;
            setDeviceList(ds);
            setRequiredDevices(ds.find((d) => d.key === 'BioHub' && d.value));
        }
    }, [currentOrg])

    useEffect(() => {
        let form;
        const onFormChange = (key, value, field, valid) => {
            if (key === 'bioReport') {
                if (form.data['email'].onChange) {
                    form.data['email'].onChange(form.data['email'].value);
                }
            }
            if (userCoreFields.hasOwnProperty(key)) {
                let u = userCore;
                if (key.search('bio.') !== -1) {
                    let k = key.replace('bio.', '');
                    if (!u.hasOwnProperty('bio')) {
                        u['bio'] = {};
                    }
                    u['bio'][k] = value;
                }else{
                    u[key] = value;
                }
                setUserCore(u);
            }else if (patientFields.hasOwnProperty(key)) {
                let p = patient;
                p[key] = value;
                setPatient(p);
            }
            setDataFields(prevState => ({
                ...prevState,
                [key]: field
            }));
            setFormValid(valid);
        };

        if (patientid && !!person) {
            // Actual Patient data
            const {dateOfBirth, EMPI, MRN, address, contactCareGiver, phones, emergencyContacts, bioReport, requiredDevices} = person;
            // Actual User core data
            const {email, bio} = person.user;
            if (!!phones && phones.mobile) {
                // For input replace +1
                phones.mobile = phones.mobile.replace('+1', '');
            }

            form = FormValidation(fields, onFormChange);

            // patient
            form.setValue('dateOfBirth', dateOfBirth);
            form.setValue('EMPI', EMPI || '');
            form.setValue('MRN', MRN || '');
            form.setValue('contactCareGiver', contactCareGiver);
            form.setValue('bioReport', bioReport);
            const list = requiredDevices? requiredDevices : [];
            setRequiredDevices(list.length!==0);

            let _address = !!address && Array.isArray(address) ? address : [];
            for (let i2 = 0; i2 < _address.length; i2++) {
                let type = _address[i2].type;
                if (_address[i2].type === 'Office') {
                    // The Office value was changed to Work
                    type = 'Work';
                } else if (_address[i2].type === 'Clinic') {
                    // the clinic value is no longer available
                    type = 'Home';
                }
                _address[i2].type = type;
            }
            form.setValue('address', _address);

            let _phones = !!phones && Array.isArray(phones) ? phones : [];
            for (let i = 0; i < _phones.length; i++) {
                let label = _phones[i].label;
                if (_phones[i].label === 'Office') {
                    // The Office value was changed to Work
                    label = 'Work';
                } else if (_phones[i].label === 'Clinic') {
                    // the clinic value is no longer available
                    label = 'Mobile';
                }
                _phones[i].label = label;
            }
            form.setValue('phones', _phones);
            form.setValue('emergencyContacts', !!emergencyContacts && Array.isArray(emergencyContacts) ? emergencyContacts : []);

            // userCore
            form.setValue('bio.lastname', !!bio && bio.lastname ? bio.lastname : '');
            form.setValue('bio.firstname', !!bio && bio.firstname ? bio.firstname : '');
            if (bio && bio.gender) {
                form.setValue('bio.gender', bio.gender);
            }
            if (email) {
                form.setValue('email', email);
            }

            setDataFields(form.data);
        } else {
            form = FormValidation(fields, onFormChange);
            setDataFields(form.data);
        }
    // >> userCore and patient might be dependencies, but seem to mutate correctly
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fields, person, match]);

    useQuery(gqlSchemaLibrary.organizations.query.getCurrentOrg, {
        onCompleted: ({ getCurrentOrg }) => {
            if (getCurrentOrg && getCurrentOrg.defaultCountry) {
                setDefaultCountry(getCurrentOrg.defaultCountry);
            } else if(process.env.DEFAULT_COUNTRY){
                setDefaultCountry(process.env.DEFAULT_COUNTRY);
            } else {
                setDefaultCountry('us');
            }
            setCurrentOrg(getCurrentOrg);
        },
        fetchPolicy: 'cache-and-network'
    });

    const showErrors = (e) => {
        const errors = e.graphQLErrors;
        const emailError = errors.find((e) => e.extensions && e.extensions.length !== 0 && e.extensions[0] === "email");
        if (emailError) {
            let field = dataFields.email;
            field.error = true;
            field.errorMessage = emailError.message;
            setDataFields(prevState => ({
                ...prevState,
                ["email"]: field
            }));
        }
        __setSubmitting(false);
    }

    const [createPatient] = useMutation(gqlSchemaLibrary.patients.mutations.createPatient, {
        onCompleted: ({ createPatient }) => {
            navigate(`/participants`)
        },
        onError: showErrors
    });

    const [updateCurrentPatient] = useMutation(gqlSchemaLibrary.patients.mutations.editPatient, {
        onCompleted: (onUpdated) => {
            navigate(`/participants`);
        },
        onError: showErrors
    });

    const [getPatientToEdit] = useLazyQuery(gqlSchemaLibrary.patients.queries.getPerson, {
        onCompleted: ({ getPerson }) => {
            if (currentUser.selectedOrgId !== getPerson.organizationId) {
                navigate(-1);
            }
            // Update current inputs to edit patient, disable inputs where necessary
            setPerson(clearGqlTypename(getPerson));
            setEdittingIds({
                userId: getPerson.user._id,
                patientId: getPerson._id
            })
        },
        fetchPolicy: 'cache-and-network'
    })

    // Check if this is an edit call
    useEffect(() => {
        if (patientid) {
            setIsEditting(true);
            getPatientToEdit({
                variables: {
                    personId: patientid
                }
            })
        }
    }, [match, getPatientToEdit]);

    const cancelParticipant = () => {
        AuditTrail.sendLog(event, {tag: 'button', event: 'onClick', action: 'button cancel form participant'});
        navigate(-1);
    }

    const validateAndSubmit = () => {
        if (!currentUser.userRoles.userPatients.edit && patientid) {
            return;
        } else if (!currentUser.userRoles.userPatients.create) {
            return;
        }
        if (!formValid || __submitting) {
            return; // Not valid yet or submitting
        }

        patient.requiredDevices = (requiredDevices? deviceList.map((d) => d.key) : []);

        __setSubmitting(true);
        if (isEditting || patientid) {
            if (!edittingIds || !(edittingIds.userId && edittingIds.patientId)) {
                console.error('No Editting IDS exist!');
                __setSubmitting(false);
                return;
            }
            updateCurrentPatient({
                variables: {
                    patientToEdit: edittingIds,
                    userCore: userCore,
                    patient: patient
                }
            })
        } else {
            createPatient({
                variables: {
                    userCore: userCore,
                    patient: patient
                }
            })
        }
    }

    const genderOptions = [
        { value: 'female', label: t('common:gender.female') },
        { value: 'male', label: t('common:gender.male') },
        { value: 'other', label: t('common:gender.other') },
    ]

    const renderNextButton = () => {
        return (
                <Button disabled={!formValid} className="primary smallPad" handleClick={validateAndSubmit}>
                    <div className={`progressButton ${__submitting ? 'submitting' : ''}`}>
                        {__submitting ? <div className="_progressing"></div> : ''}
                        <span>{patientid ? t('common:done') : t('common:register')}</span>
                    </div>
                </Button>
            )
    }

    const displayDeviceOption = (key) => {
        return deviceList.find((d) => d.key === key);
    }

    const requiredDeviceOption = (key) => {
        return deviceList.find((d) => d.key === key && d.value);
    }

    const renderPatientInfo = () => {
        return (
            <div className="formContent">
                <div>
                    <h3>
                        {t('participants-info')}
                    </h3>
                    <div className="row two">
                        <Input required={dataFields['bio.lastname'].required} error={dataFields['bio.lastname'].error} value={dataFields['bio.lastname'].value} onChange={dataFields['bio.lastname'].onChange} placeholder={t('users:last-name')} label={t('users:last-name')} event={event} />
                        <Input required={dataFields['bio.firstname'].required} error={dataFields['bio.firstname'].error} value={dataFields['bio.firstname'].value} onChange={dataFields['bio.firstname'].onChange} placeholder={t('users:first-name')} label={t('users:first-name')} event={event} />
                    </div>
                    <div className="row two left">
                        <div className="row two _date">
                            <DateSelect event={event} required={dataFields['dateOfBirth'].required} error={dataFields['dateOfBirth'].error} value={dataFields['dateOfBirth'].value} onChange={dataFields['dateOfBirth'].onChange} asNumber={true} />
                            <Select required={dataFields['bio.gender'].required} error={dataFields['bio.gender'].error} value={dataFields['bio.gender'].value} onChange={dataFields['bio.gender'].onChange} label={t('common:gender.title')} placeholder={'--'} options={genderOptions} event={event} />
                        </div>
                        <div className="row two">
                            <Input value={dataFields['EMPI'].value} onChange={dataFields['EMPI'].onChange} placeholder={t('empi').toUpperCase()} label={t('empi').toUpperCase()} event={event} />
                            <Input value={dataFields['MRN'].value} onChange={dataFields['MRN'].onChange} placeholder={t('mrn').toUpperCase()} label={t('mrn').toUpperCase()} event={event} />
                        </div>
                    </div>
                    <div className="row two">
                        <Input required={dataFields['email'].required} regexTest={/^\S+@\S+\.\S+$/} error={dataFields['email'].error} errorMessage={dataFields['email'].errorMessage} value={dataFields['email'].value} onChange={dataFields['email'].onChange} placeholder={t('users:email')} label={t('users:email')} event={event} />
                        <Checkbox label={t('contact-caregiver')} onChange={dataFields['contactCareGiver'].onChange} bool={dataFields['contactCareGiver'].value} event={event} />
                    </div>
                    <CreateUserPhoneList event={event} prefix="patient" defaultCountry={defaultCountry} required={dataFields['phones'].required} error={dataFields['phones'].error} phones={dataFields['phones'].value} onChange={dataFields['phones'].onChange} displaySMSAlerts={false} />
                    <div className="row _extra">
                        {
                            displayDeviceOption('BioHub')?
                                <Checkbox label={t('required-hub')} onChange={setRequiredDevices} bool={requiredDevices} disabled={requiredDeviceOption('BioHub')} event={event} /> :  ''
                        }
                    </div>
                </div>
                <div className="divider"></div>
                <div>
                    <h3>
                        {t('users:address')}
                    </h3>
                    <CreateUserAddressList event={event} error={dataFields['address'].error} defaultCountry={defaultCountry} address={dataFields['address'].value} onChange={dataFields['address'].onChange} arrayRequired={dataFields['address'].arrayRequired} />
                </div>
                <div className="divider"></div>
                <div>
                    <h3>
                        {t('users:contacts')}
                    </h3>
                    <PatientEmergencyContacts event={event} defaultCountry={defaultCountry} error={dataFields['emergencyContacts'].error} emergencyContacts={dataFields['emergencyContacts'].value} onChange={dataFields['emergencyContacts'].onChange} />
                </div>
            </div>
        )
    }


    return (
        <div id="PatientAssign">
            <div className="headerTitle">
                <div className="imageUploadHolder">
                    <PatientAvatarUpload bio={dataFields['bio.lastname'].value ? dataFields['bio.lastname'].value : {}} hideIfNone={false} />
                </div>
                <div className="bioHolder">
                    <h2>{dataFields['bio.lastname'].value}{!!dataFields['bio.lastname'].value ? ',' : ''} {dataFields['bio.firstname'].value}</h2>
                    <p className="gender">
                        {dataFields['bio.gender'].value}
                    </p>
                </div>
                <Button className="tertiary white" handleClick={cancelParticipant}>
                    {t('common:cancel')}
                </Button>
                {renderNextButton()}
            </div>
            <div className="patientsections scrollBar">
                <div className="subSections">

                </div>
                {renderPatientInfo()}
            </div>
        </div>
    )
}

export default PageViewLog(PatientAssign);
